import React, { useEffect, useState } from "react";
import { useForm, OtpInput, validatePhone, styles } from "./Forms";

export default function OtpVerification() {
  const { phone, setPhone, sendOtp, verifyOtp, setPhase, loadForm, vendor } =
    useForm();

     const [logoError, setLogoError] = useState(false);
     useEffect(() => {
       setLogoError(false);
     }, [vendor?.logo]);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [authErr, setAuthErr] = useState(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const handlePhoneChange = (v) => {
    setPhone(v);
    if (authErr) setAuthErr(null);
    if (otpSent) {
      setOtpSent(false);
      setOtp("");
      setResendIn(0);
    }
  };

  const handleSendOtp = async () => {
    const err = validatePhone(phone);
    if (err) {
      setAuthErr(err);
      return;
    }
    setSending(true);
    setAuthErr(null);
    try {
      await sendOtp(phone);
      setOtpSent(true);
      setResendIn(30);
    } catch (e) {
      setAuthErr(e?.response?.data?.message || "Failed to send OTP");
    } finally {
      setSending(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!otpSent) {
      setAuthErr("Please tap Send OTP first");
      return;
    }
    if (otp.length < 4) {
      setAuthErr("Enter the OTP we sent you");
      return;
    }
    setVerifying(true);
    setAuthErr(null);
    try {
      await verifyOtp(phone, otp);
      setPhase("success");
      await loadForm();
      setTimeout(() => setPhase("form"), 1500);
    } catch (e) {
      setAuthErr(e?.response?.data?.message || "Invalid OTP");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <div style={styles.authCard}>
        <div style={styles.authHero}>
          {vendor?.logo && !logoError ? (
            <img
              src={vendor.logo}
              alt={vendor.name || "Logo"}
              style={styles.authHeroLogo}
              onError={() => setLogoError(true)}
            />
          ) : vendor?.name ? (
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: 16,
                background: "#EA580C",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 48,
                fontWeight: 700,
                marginBottom: 16,
                flexShrink: 0,
              }}
            >
              {vendor.name.trim().charAt(0).toUpperCase()}
            </div>
          ) : null}
          <p style={styles.authHeroWelcome}>
            Welcome to {vendor?.name || "our platform"}
          </p>
        </div>

        <form onSubmit={onSubmit} style={styles.form} noValidate>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="auth-phone">
              Mobile Number <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <div style={styles.phoneRow}>
              <span style={styles.phonePrefix}>+91</span>
              <input
                id="auth-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                value={phone}
                onChange={(e) =>
                  handlePhoneChange(e.target.value.replace(/\D/g, ""))
                }
                placeholder="Enter 10-digit number"
                maxLength={10}
                disabled={verifying}
                style={{
                  ...styles.bigInput,
                  borderRadius: "0 8px 8px 0",
                  borderLeft: "none",
                  opacity: verifying ? 0.6 : 1,
                }}
              />
            </div>

            {!otpSent && (
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={
                  sending || verifying || phone.length !== 10 || resendIn > 0
                }
                style={{
                  ...styles.sendOtpBtn,
                  opacity:
                    sending || verifying || phone.length !== 10 || resendIn > 0
                      ? 0.5
                      : 1,
                  cursor:
                    sending || verifying || phone.length !== 10 || resendIn > 0
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {sending ? "Sending…" : "Send OTP"}
              </button>
            )}

            {otpSent && (
              <span style={styles.successHint}>✓ OTP sent to +91 {phone}</span>
            )}
          </div>

          {otpSent && (
            <div style={styles.field}>
              <label style={styles.label}>
                Enter OTP <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <OtpInput
                value={otp}
                length={4}
                disabled={!otpSent || verifying}
                autoFocus={otpSent}
                onChange={(v) => {
                  setOtp(v);
                  if (authErr) setAuthErr(null);
                }}
              />
              <p style={styles.resendHint}>
                Didn't get the code?{" "}
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sending || resendIn > 0}
                  style={{
                    ...styles.resendLink,
                    opacity: sending || resendIn > 0 ? 0.5 : 1,
                    cursor: sending || resendIn > 0 ? "not-allowed" : "pointer",
                  }}
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend OTP"}
                </button>
              </p>
            </div>
          )}

          {authErr && <div style={styles.errorBox}>⚠️ {authErr}</div>}

          {otpSent && (
            <button
              type="submit"
              disabled={verifying || otp.length < 4}
              style={{
                ...styles.primaryBtn,
                opacity: verifying || otp.length < 4 ? 0.5 : 1,
                cursor: verifying || otp.length < 4 ? "not-allowed" : "pointer",
              }}
            >
              {verifying ? "Verifying…" : "Verify & Proceed "}
            </button>
          )}
        </form>
      </div>
    </>
  );
}
